import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileCheck2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SOL_VERSIONS = ["0.8.24", "0.8.20", "0.8.19", "0.8.17", "0.8.10", "0.7.6", "0.6.12", "0.5.16", "0.4.26"];
const LICENSES = ["MIT", "GPL-3.0", "Apache-2.0", "BSD-2-Clause", "BSD-3-Clause", "MPL-2.0", "Unlicense", "None"];

export default function VerifyContract() {
  const nav = useNavigate();
  const [f, setF] = useState({
    address: "", contract_name: "", compiler_version: "0.8.20", license: "MIT",
    optimization: false, runs: 200, source_code: "", constructor_args: "", abi: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contracts/verify", f);
      toast.success("Contract verified successfully");
      nav(`/address/${f.address.toLowerCase()}`);
    } catch (ex) {
      const d = ex.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-4">
        <div className="ks-icon-square"><FileCheck2 size={18}/></div>
        <div>
          <h1 className="text-xl font-semibold" data-testid="verify-title">Verify &amp; Publish Contract Source Code</h1>
        </div>
      </div>

      <form onSubmit={submit} className="ks-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Contract Address</Label>
          <Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="0x…" required data-testid="verify-address"/>
        </div>
        <div>
          <Label>Contract Name</Label>
          <Input value={f.contract_name} onChange={(e) => set("contract_name", e.target.value)} placeholder="MyToken" required data-testid="verify-name"/>
        </div>
        <div>
          <Label>Compiler Version</Label>
          <Select value={f.compiler_version} onValueChange={(v) => set("compiler_version", v)}>
            <SelectTrigger data-testid="verify-version"><SelectValue/></SelectTrigger>
            <SelectContent>
              {SOL_VERSIONS.map((v) => <SelectItem key={v} value={v}>v{v}+commit</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Open Source License Type</Label>
          <Select value={f.license} onValueChange={(v) => set("license", v)}>
            <SelectTrigger data-testid="verify-license"><SelectValue/></SelectTrigger>
            <SelectContent>
              {LICENSES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-4">
          <div className="flex items-center gap-2">
            <Switch checked={f.optimization} onCheckedChange={(v) => set("optimization", v)} data-testid="verify-opt"/>
            <Label>Optimization</Label>
          </div>
          {f.optimization && (
            <div><Label>Runs</Label><Input type="number" value={f.runs} onChange={(e) => set("runs", Number(e.target.value))} className="w-24"/></div>
          )}
        </div>
        <div className="md:col-span-2">
          <Label>Solidity Source Code</Label>
          <Textarea rows={12} value={f.source_code} onChange={(e) => set("source_code", e.target.value)} className="font-mono text-xs" placeholder="// SPDX-License-Identifier: MIT&#10;pragma solidity ^0.8.20;&#10;&#10;contract MyToken { }" required data-testid="verify-source"/>
        </div>
        <div className="md:col-span-2">
          <Label>Contract ABI (JSON, optional)</Label>
          <Textarea rows={4} value={f.abi} onChange={(e) => set("abi", e.target.value)} className="font-mono text-xs" placeholder='[{"type":"function", ... }]' data-testid="verify-abi"/>
        </div>
        <div className="md:col-span-2">
          <Label>Constructor Arguments (ABI-encoded, optional)</Label>
          <Input value={f.constructor_args} onChange={(e) => set("constructor_args", e.target.value)} placeholder="0x…" data-testid="verify-args"/>
        </div>
        <div className="md:col-span-2 flex justify-end gap-2 pt-3 border-t border-[var(--ks-border)]">
          <Button type="button" variant="outline" onClick={() => nav("/")}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)]" data-testid="verify-submit">
            {busy ? "Verifying…" : "Verify and Publish"}
          </Button>
        </div>
      </form>
    </Layout>
  );
}
